type SafariWindow = typeof globalThis & {
    webkitAudioContext?: typeof AudioContext;
};

const silentStoryUnlockSource = "data:audio/wav;base64," +
    "UklGRrQBAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YZABAACAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICA";

export class GpsTriggerAudioActivation {
    private context?: AudioContext;
    private resumePromise?: Promise<void>;
    private cueBufferPromise?: Promise<AudioBuffer>;
    private cueSourceUrl?: string;
    private storyUnlockStarted = false;
    private interactionReceived = false;

    get hasReceivedInteraction(): boolean {
        return this.interactionReceived;
    }

    arm(cueSourceUrl: string, storyAudio: HTMLAudioElement): void {
        this.interactionReceived = true;
        this.armCue(cueSourceUrl);
        this.armStory(storyAudio);
    }

    async playCue(cueSourceUrl: string): Promise<void> {
        const context = this.context;
        if (!context || !this.resumePromise) {
            throw new DOMException("Audio playback requires a page interaction.", "NotAllowedError");
        }

        await this.resumePromise;
        if (context.state !== "running") {
            throw new DOMException("Audio playback is not currently permitted.", "NotAllowedError");
        }

        const buffer = await this.loadCue(context, cueSourceUrl);
        if (context.state !== "running") {
            throw new DOMException("Audio playback is not currently permitted.", "NotAllowedError");
        }

        await new Promise<void>((resolve, reject) => {
            const source = context.createBufferSource();
            source.buffer = buffer;
            source.connect(context.destination);
            source.onended = () => resolve();
            try {
                source.start();
            } catch (error) {
                reject(error);
            }
        });
    }

    private armCue(cueSourceUrl: string): void {
        const AudioContextConstructor = globalThis.AudioContext ??
            (globalThis as SafariWindow).webkitAudioContext;
        if (!AudioContextConstructor) {
            return;
        }

        this.context ??= new AudioContextConstructor();
        this.resumePromise = this.context.state === "running"
            ? Promise.resolve()
            : this.context.resume();
        void this.loadCue(this.context, cueSourceUrl).catch(() => undefined);
    }

    private armStory(storyAudio: HTMLAudioElement): void {
        if (this.storyUnlockStarted || storyAudio.dataset.mediaId) {
            return;
        }

        this.storyUnlockStarted = true;
        storyAudio.src = silentStoryUnlockSource;
        storyAudio.play().catch(() => {
            this.storyUnlockStarted = false;
        });
    }

    private loadCue(context: AudioContext, cueSourceUrl: string): Promise<AudioBuffer> {
        if (this.cueBufferPromise && this.cueSourceUrl === cueSourceUrl) {
            return this.cueBufferPromise;
        }

        this.cueSourceUrl = cueSourceUrl;
        const cueBufferPromise = fetch(cueSourceUrl, {
            cache: "force-cache",
            credentials: "same-origin"
        }).then(response => {
            if (!response.ok) {
                throw new Error(`The GPS cue request failed with status ${response.status}.`);
            }
            return response.arrayBuffer();
        }).then(bytes => context.decodeAudioData(bytes));
        this.cueBufferPromise = cueBufferPromise;
        void cueBufferPromise.catch(() => {
            if (this.cueBufferPromise === cueBufferPromise) {
                this.cueBufferPromise = undefined;
                this.cueSourceUrl = undefined;
            }
        });
        return cueBufferPromise;
    }
}
